import React from 'react'
import {useIntl} from 'react-intl'
import {Box, BoxProps, Grid, GridItem, GridItemProps, Image, ImageProps, StackProps, useMultiStyleConfig} from '@chakra-ui/react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useViewport} from '_customHooks'
import {HideBelowViewport} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import Menu from '../menu'
import TopbarLeft from './partials/topbar-left'
import TopbarRight from './partials/topbar-right'
import {Link} from 'react-router-dom'

interface TopbarProps {
    isTransparentMode: boolean
    isAtTop: boolean
    isSearchSectionEnabled: boolean
    isHomepage: boolean
}

/**
 * @name Topbar
 *
 * @component
 *
 * @description
 * The `Topbar` component is responsible for rendering the header section,
 * which includes left and right header links, the menu, and the brand logo.
 *
 * @param {TopbarProps} props - The properties object.
 * @param {boolean} props.isTransparentMode - Determines if the header is in transparent mode.
 * @param {boolean} props.isAtTop - Indicates if the header is positioned at the top.
 * @param {boolean} props.isSearchSectionEnabled - Specifies if the search section is enabled in the header.
 * @param {boolean} props.isHomepage - Identifies if the current page is the homepage.
 *
 * @returns {React.FC<TopbarProps>} - Returns the rendered `Topbar` component.
 */
const Topbar: React.FC<TopbarProps> = ({isTransparentMode, isAtTop, isSearchSectionEnabled, isHomepage}) => {
    const {locale} = useMultiSite()
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('Topbar', {isTransparentMode, isAtTop}) as Record<string, StackProps & BoxProps & ImageProps & GridItemProps>
    const {isDesktop} = useViewport()

    return (
        <Grid data-testid="topbar" {...styles.topbar}>
            <GridItem area="left">
                <TopbarLeft />
            </GridItem>

            <GridItem area="right">
                <TopbarRight />
            </GridItem>

            <Box as={isHomepage ? 'h1' : 'div'} {...styles.logoContainer}>
                <Link to={`/${locale.alias}/`}>
                    <Image
                        data-testid="logo-image"
                        {...styles.logoImage}
                        src={getAssetUrl('static/img/logo.svg')}
                        alt={formatMessage({id: 'global.storename', defaultMessage: 'Brunello Cucinelli - official boutique'})}
                    />
                </Link>
            </Box>

            {isDesktop && (
                <HideBelowViewport breakpoint="lg" as={GridItem} area="menu">
                    <Menu isAtTop={isAtTop} isTransparentMode={isTransparentMode} isSearchSectionEnabled={isSearchSectionEnabled} />
                </HideBelowViewport>
            )}
        </Grid>
    )
}

/**
 * Custom equal function to memoize the `Topbar` component.
 *
 * @param {TopbarProps} prevProps
 * @param {TopbarProps} nextProps
 * @returns {boolean} - True if the props remained the same, false otherwise.
 */
const areEqual = (prevProps: TopbarProps, nextProps: TopbarProps): boolean => {
    return (
        prevProps.isAtTop === nextProps.isAtTop &&
        prevProps.isTransparentMode === nextProps.isTransparentMode &&
        prevProps.isSearchSectionEnabled === nextProps.isSearchSectionEnabled &&
        prevProps.isHomepage === nextProps.isHomepage
    )
}

export default React.memo(Topbar, areEqual)
